import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Filter, Warehouse } from 'lucide-react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import moment from 'moment';
import Sidebar1 from "components/Sidebar1";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { fetchWithToken } from "api/ApiHandler";

const getInitials = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "SP";
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
};

const formatDate = (value) => {
  if (!value) return "-";
  const m = moment.utc(value);
  return m.isValid() ? m.format("DD/MM/YYYY") : "-";
};

const statusMeta = (status) => {
  switch ((status || "").toLowerCase()) {
    case "active":
      return { label: "Active", className: "text-green-500" };
    case "pending":
      return { label: "Pending", className: "text-yellow-500" };
    default:
      return { label: status || "Unknown", className: "text-gray-400" };
  }
};

const handleAuthError = (error) => {
  const status = error?.response?.status;
  const msg = error?.response?.data?.msg;
  if (status === 401 || msg === "Invalid Token") {
    localStorage.clear();
    window.location.href = "/admin-login";
    return true;
  }
  return false;
};

export default function ServiceProvider() {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  const [providers, setProviders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const loadProviders = async () => {
    try {
      setIsLoading(true);
      setLoadError("");
      const data = await fetchWithToken("/admin/get-all-service-providers");
      setProviders(Array.isArray(data?.providers) ? data.providers : []);
    } catch (error) {
      console.error("Fetch service providers error:", error.response?.data || error);
      if (handleAuthError(error)) return;
      setLoadError("Unable to load service providers.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadProviders();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  // Search & Filter Logic
  const filteredProviders = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return providers.filter((provider) => {
      const matchesSearch =
        (provider.businessName || "").toLowerCase().includes(term) ||
        (provider.fullName || "").toLowerCase().includes(term) ||
        (provider.businessEmail || "").toLowerCase().includes(term);
      const matchesStatus =
        statusFilter === "All" || (provider.status || "").toLowerCase() === statusFilter.toLowerCase();
      return matchesSearch && matchesStatus;
    });
  }, [providers, searchTerm, statusFilter]);

  // --- PAGINATION LOGIC ---
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredProviders.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredProviders.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const recentProviders = useMemo(() => {
    return [...providers]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 5);
  }, [providers]);

  const toggleFilter = () => {
    const statuses = ["All", "Active", "Pending"];
    const currentIndex = statuses.indexOf(statusFilter);
    setStatusFilter(statuses[(currentIndex + 1) % statuses.length]);
  };

  return (
    <div className="flex h-screen bg-[#F9FAFB] overflow-hidden font-sans">

      {/* 1. Integrated Sidebar */}
      <Sidebar1 isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} />

      {/* Main Content Area */}
      <main className="flex-1 p-5 overflow-y-auto" style={{paddingLeft:10}}>
        <header className="flex items-center justify-between p-4 lg:px-5" style={{paddingLeft:10}}>
          <h1 style={{fontFamily:'NeueMontreal'}} className="text-2xl lg:text-3xl font-medium text-gray-900">
            User Management (Service Providers)
          </h1>
          <div className="flex items-center gap-4">

             {/* <div className="w-10 h-10 rounded-xl overflow-hidden border shadow-sm">
                <img src="https://i.pravatar.cc/150?u=admin" alt="Admin" className="w-full h-full object-cover" />
             </div> */}
          </div>
        </header>


        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto  pt-0 pl-3 pr-3">
          <div className="grid grid-cols-[65%_calc(35%-2rem)] md:grid-cols-1 lg:grid-cols-[65%_calc(35%-2rem)] gap-5">

            {/* Grid Section */}
            <div style={{background:'white',boxShadow:'0px 0px 3px 0px #c3c3c3d1'}} className="xl:col-span-2 bg-white p-5 lg:p-8 rounded-[32px] border border-gray-50 shadow-sm self-start">

              <div className="flex items-center justify-between mb-8 gap-4">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <InputText
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by business name or email"
                    className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-1 focus:ring-red-400"
                  />
                </div>
                <Button
                  icon={<Filter size={18} className={statusFilter !== 'All' ? 'text-white' : 'text-[#EA352B]'} />}
                  onClick={toggleFilter}
                  className={`p-2.5 rounded-xl border transition-all ${statusFilter !== 'All' ? 'bg-red-500' : 'bg-white'}`}
                  tooltip={`Current: ${statusFilter}`}
                />
              </div>

              <div className="grid grid-cols-3 md:grid-cols-2 sm:grid-cols-1 lg:grid-cols-3 gap-4">
                {isLoading ? (
                  Array.from({ length: 9 }).map((_, i) => <ProviderCardSkeleton key={i} />)
                ) : loadError ? (
                  <p className="col-span-full text-center text-sm text-red-400 py-10">{loadError}</p>
                ) : currentItems.length === 0 ? (
                  <p className="col-span-full text-center text-sm text-gray-400 py-10">No service providers found.</p>
                ) : currentItems.map((provider) => {
                  const status = statusMeta(provider.status);
                  return (
                    <div style={{boxShadow:"rgb(172 168 168 / 82%) 0px 0px 2px 0px"}}
                      key={provider._id}
                      onClick={() => navigate(`/user-management/service-provider/${provider._id}`)}
                      className="bg-[#F6F6F6] p-4 rounded-[20px] border border-transparent hover:border-red-100 hover:shadow-lg transition-all cursor-pointer group"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center text-[#EA352B] font-semibold text-sm shrink-0">
                          {getInitials(provider.businessName)}
                        </div>
                        <div className="min-w-0">
                          <p style={{fontFamily:'NeueMontreal'}} className="text-[14px] font-medium text-black group-hover:text-red-600 truncate">{provider.businessName || "Unnamed Business"}</p>
                          <p style={{fontFamily:'NeueMontreal'}} className="text-[12px] text-black font-light truncate">{provider.fullName || provider.businessEmail}</p>
                        </div>
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t border-gray-200/50">
                        <span style={{fontFamily:'NeueMontreal'}} className="text-[11px] text-[black] font-light">Status</span>
                        <span style={{fontFamily:'NeueMontreal'}} className={`text-[14px] font-medium ${status.className}`}>
                          {status.label}
                        </span>
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t border-gray-200/50">
                        <span style={{fontFamily:'NeueMontreal'}} className="text-[11px] text-[black] font-light">Joined</span>
                        <span style={{fontFamily:'NeueMontreal'}} className="text-[12px] text-black font-light">{formatDate(provider.createdAt)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* --- PAGINATION SECTION --- */}
              {totalPages > 1 && (
                <div className="flex justify-end mt-10 gap-2 items-center">
                    <button
                      disabled={currentPage === 1}
                      onClick={() => paginate(currentPage - 1)}
                      className={`text-xs font-medium px-2 py-1 transition ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:text-black'}`}
                    >
                      ← Previous
                    </button>

                    {Array.from({ length: totalPages }, (_, i) => (
                      <button
                        key={i + 1}
                        style={{fontFamily:"'NeueMontreal', sans-serif",color:currentPage === i + 1 ? 'white' : 'black'}}
                        onClick={() => paginate(i + 1)}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-medium transition ${
                          currentPage === i + 1
                          ? 'bg-[#EA352B] text-white shadow-lg font-bold'
                          : 'hover:bg-gray-100 text-gray-500'
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}

                    <button
                      disabled={currentPage === totalPages}
                      onClick={() => paginate(currentPage + 1)}
                      className={`text-xs font-bold px-2 py-1 transition ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-black'}`}
                    >
                      Next →
                    </button>
                </div>
              )}
            </div>

            {/* Recently Joined Sidebar */}
            <div style={{background:'white',boxShadow:'0px 0px 3px 0px #c3c3c3d1'}} className="bg-white p-6 rounded-[32px] border border-gray-50 shadow-sm h-fit">
              <div className="flex items-center gap-2 mb-6">
                <div className="p-1.5 bg-red-50 rounded-lg"><Warehouse size={16} className="text-[#EA352B]" /></div>
                <h3 style={{fontFamily:'NeueMontreal'}} className="text-[20px] font-medium text-black">Recently Joined</h3>
              </div>
              <div className="space-y-4">
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => <RecentProviderSkeleton key={i} />)
                ) : recentProviders.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-6">No service providers yet.</p>
                ) : recentProviders.map((provider) => {
                  const status = statusMeta(provider.status);
                  return (
                    <div
                      key={provider._id}
                      style={{boxShadow:'rgb(172 168 168 / 82%) 0px 0px 2px 0px'}}
                      onClick={() => navigate(`/user-management/service-provider/${provider._id}`)}
                      className="bg-[#F9FAFB] p-3 rounded-2xl flex items-center justify-between group cursor-pointer hover:shadow-md transition"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center text-[#EA352B] font-semibold text-sm shrink-0">
                          {getInitials(provider.businessName)}
                        </div>
                        <div className="min-w-0">
                          <p style={{fontFamily:'NeueMontreal'}} className="text-[14px] font-medium text-black truncate">{provider.businessName || "Unnamed Business"}</p>
                          <p style={{fontFamily:'NeueMontreal'}} className="text-[11px] text-black font-light">{formatDate(provider.createdAt)}</p>
                        </div>
                      </div>
                      <span className={`text-[10px] font-semibold px-2 py-1 rounded-full shrink-0 ${provider.status === 'active' ? 'bg-green-50 text-green-600' : 'bg-yellow-50 text-yellow-600'}`}>
                        {status.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function ProviderCardSkeleton() {
  return (
    <div className="bg-[#F6F6F6] p-4 rounded-[20px]">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-gray-200 animate-pulse" />
        <div className="flex-1">
          <div className="h-3 w-24 bg-gray-200 rounded animate-pulse mb-2" />
          <div className="h-2 w-20 bg-gray-200 rounded animate-pulse" />
        </div>
      </div>
      <div className="flex justify-between items-center pt-2 border-t border-gray-200/50 mb-2">
        <div className="h-2 w-14 bg-gray-200 rounded animate-pulse" />
        <div className="h-2 w-12 bg-gray-200 rounded animate-pulse" />
      </div>
      <div className="flex justify-between items-center pt-2 border-t border-gray-200/50">
        <div className="h-2 w-14 bg-gray-200 rounded animate-pulse" />
        <div className="h-2 w-16 bg-gray-200 rounded animate-pulse" />
      </div>
    </div>
  );
}

function RecentProviderSkeleton() {
  return (
    <div className="bg-[#F9FAFB] p-3 rounded-2xl flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg bg-gray-200 animate-pulse shrink-0" />
      <div className="flex-1">
        <div className="h-3 w-24 bg-gray-200 rounded animate-pulse mb-2" />
        <div className="h-2 w-16 bg-gray-200 rounded animate-pulse" />
      </div>
    </div>
  );
}
